/**
 * API Client - HTTP client para comunicação com o backend
 *
 * Ordem de prioridade para a URL da API:
 * 1. window.__ENV__.VITE_API_URL - config.js injetado em runtime (Docker/EasyPanel)
 * 2. import.meta.env.VITE_API_URL - variável em tempo de build (Vite)
 * 3. Fallback localhost
 */

function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && (window as any).__ENV__?.VITE_API_URL) {
    return (window as any).__ENV__.VITE_API_URL;
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
}

const API_BASE_URL = getApiBaseUrl();

export interface ApiError {
  success: false;
  error: string;
}

/**
 * Erro HTTP da API que preserva o `code` e os campos extras da resposta
 * (ex.: `{ success: false, error, code: 'CARGO_EM_USO', total_pessoas: 3 }`).
 * Continua sendo um `Error` comum para quem só lê `.message`.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly payload: Record<string, unknown>;

  constructor(message: string, status: number, payload: Record<string, unknown>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = typeof payload.code === 'string' ? payload.code : undefined;
    this.payload = payload;
  }
}

/** Evento disparado após toda gravação bem-sucedida (POST/PUT/PATCH/DELETE). O tour de primeiros passos
 * escuta pra atualizar o progresso sem depender de cada tela avisar. */
export const EVENTO_DADOS_ALTERADOS = 'kora:dados-alterados';

const avisarAlteracao = (method: string, endpoint: string) => {
  if (method === 'GET' || typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENTO_DADOS_ALTERADOS, { detail: { method, endpoint } }));
};

export class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private getToken(): string | null {
    return localStorage.getItem('okr_auth_token');
  }

  /** Substitui o token da sessão (ex.: após trocar a senha, que invalida o token anterior). */
  setToken(token: string): void {
    localStorage.setItem('okr_auth_token', token);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Depois do token padrão — permite que uma chamada específica (ex: validateToken)
    // sobrescreva o Authorization sem precisar mexer no localStorage compartilhado entre abas.
    Object.assign(headers, options.headers);

    const empresaAtiva = localStorage.getItem('okr_empresa_ativa');
    if (empresaAtiva) {
      headers['X-Empresa-ID'] = empresaAtiva;
    }

    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Se token inválido ou expirado, limpar e redirecionar
      if (response.status === 401) {
        localStorage.removeItem('okr_auth_token');
        // Não redirecionar aqui, deixar o componente lidar com isso
      }

      const data = await response.json();

      if (!response.ok) {
        throw new ApiRequestError(
          data.error || `Erro HTTP: ${response.status}`,
          response.status,
          typeof data === 'object' && data !== null ? data : {},
        );
      }

      avisarAlteracao(options.method ?? 'GET', endpoint);
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro de conexão com o servidor');
    }
  }

  async get<T>(endpoint: string, options?: { headers?: HeadersInit }): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers: options?.headers });
  }

  async post<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const token = this.getToken();
    const empresaAtiva = localStorage.getItem('okr_empresa_ativa');

    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (empresaAtiva) headers['X-Empresa-ID'] = empresaAtiva;

    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      let errorMessage = `Erro HTTP: ${response.status}`;
      try {
        const data = await response.json();
        errorMessage = data.error || errorMessage;
      } catch {
        // resposta binária ou vazia
      }
      throw new Error(errorMessage);
    }
    return response.blob();
  }

  async postMultipart<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = this.getToken();
    const empresaAtiva = localStorage.getItem('okr_empresa_ativa');

    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (empresaAtiva) headers['X-Empresa-ID'] = empresaAtiva;
    // Content-Type omitido: browser define o boundary automaticamente

    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, { method: 'POST', headers, body: formData });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `Erro HTTP: ${response.status}`);
    avisarAlteracao('POST', endpoint);
    return data;
  }
}

export const apiClient = new ApiClient();
